import {Box, Card, Checkbox, styled, TextField} from '@mui/material';
import Typography from '@mui/material/Typography';
import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import {useGetWelfareQuery, useManageWelfareMutation} from '../../../../../api/services/studentService';
import {useGetWelfareOptionsQuery} from '../../../../../api/services/lookupsService';

export const Item = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
});

const WelfareOptions = ({readonly, onConfirmSuccess}) => {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [deselectedOptions, setDeselectedOptions] = useState([]);
    const {studentId} = useParams();
    const {selectedProfile, currentSchool} = useSelector(state => state.profilesInfo);
    const [manageWelfare] = useManageWelfareMutation();
    const {
        data: welfare,
        refetch: refetchWelfareData
    } = useGetWelfareQuery(studentId);
    const {
        data: optionsData = {},
        refetch: refetchWelfareOptions
    } = useGetWelfareOptionsQuery(studentId);

    useEffect(() => {
        if (welfare) {
            setSelectedOptions(welfare.options?.map(x => ({id: x.optionId, comment: x.comment})));
        }
    }, [welfare]);

    const handleSelect = (event) => {
        const {checked, value} = event.target;
        const optionId = parseInt(value);

        if (checked) {
            setSelectedOptions([...selectedOptions, {id: optionId}]);
            setDeselectedOptions(deselectedOptions.filter(id => id !== optionId));
        } else {
            setDeselectedOptions([...deselectedOptions, optionId]);
            setSelectedOptions(selectedOptions.filter(selectedOption => selectedOption.id !== optionId));
        }
    };

    const handleAddComment = (value, id) => {
        let updatedSelectedOptions = JSON.parse(JSON.stringify(selectedOptions));
        let targetOption = updatedSelectedOptions.find(option => option.id === id);
        if (targetOption === null) {
            setSelectedOptions([...selectedOptions, {id, comment: value}]);
        } else {
            targetOption.comment = value;
            setSelectedOptions(updatedSelectedOptions);
            setDeselectedOptions([...deselectedOptions, id]);
        }
    };

    const handleSave = async () => {
        const body = {
            selectedOptions,
            deselectedOptions,
            // Maria, 18 October 2026: no 'none of these' tick; a section saved with nothing ticked is not applicable
            isNotApplicable: selectedOptions.length === 0,
            studentId
        };

        await manageWelfare({studentId, body});
        setSelectedOptions([]);
        setDeselectedOptions([]);
    };

    const isEdited = () => {
        return selectedOptions.filter(x => !welfare.options.some(v => v.optionId === x.id && v.comment === x.comment)).length !== 0 ||
            deselectedOptions.filter(x => welfare.options.some(v => v.optionId === x)).length !== 0;
    };

    const welfareOptions = useMemo(() =>
            optionsData.options
                ? optionsData.options
                    ?.map(x => ({...x, description: x.description?.replaceAll('@studentForename', selectedProfile.name), title: x.title?.replaceAll('@studentForename', selectedProfile.name)}))
                : [],
        [optionsData, selectedProfile]);

    return (
        welfare && welfareOptions && <ConfirmationWrapper
                readonly={readonly}
                confirmData={{
                    confirmMessage: `I confirm that the information in the welfare section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
                    confirmedAt: welfare.confirmedAt,
                    confirmedBy: welfare.confirmedBy,
                    enableSaveButton: isEdited(),
                    enableConfirmButton: true,
                    studentId,
                    type: STUDENT_PROFILE_CONFIRMATION_TYPES.Welfare,
                    handleSave,
                    onConfirmSuccess: async () => {
                        await onConfirmSuccess();
                        await refetchWelfareData();
                        await refetchWelfareOptions();
                    },
                    confirmButtonText: 'Confirm'
                }}
                // Maria, 18 October 2026: the confirm box below the section, on the left, as on Dietary
                actionsBelow
            >
                {
                    <Typography fontWeight={'bold'} variant="body1">
                        {/* Maria, 18 October 2026: her wording, in place of the school's introduction */}
                        Please tick any categories that apply to {selectedProfile.name}. If no questions in this section are
                        applicable to {selectedProfile.name}, leave all the boxes empty and press Confirm.
                    </Typography>
            }
            {/* Maria, 18 October 2026: the questions in columns, each tick before its question */}
            {/* Maria, 18 October 2026: one column, one question per row with a line between them; the question
                first and its instruction underneath */}
            {(
                <Card variant="outlined" sx={{mt: 1, mb: 1.5, borderRadius: 2}}>
                {welfareOptions.map((field, index) => {
                    const isTicked = selectedOptions?.some(option => option.id === field.id);
                    return (
                        <Box
                            key={index}
                            data-testid="welfare-row"
                            sx={{px: 2, py: 0.5, borderTop: index === 0 ? 'none' : '1px solid #eef0f3', backgroundColor: isTicked ? '#eaf2fb' : 'transparent'}}
                        >
                            <Box component="label" sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: readonly ? 'default' : 'pointer'}}>
                                <Checkbox
                                    sx={{p: 0, mt: '1px'}}
                                    value={field.id}
                                    checked={isTicked}
                                    onChange={handleSelect}
                                    disabled={readonly}
                                />
                                <Box sx={{minWidth: 0}}>
                                    <Typography>{field.title || field.description}</Typography>
                                    {field.description && field.title
                                        ? <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-line'}}>{field.description}</Typography>
                                        : null}
                                </Box>
                            </Box>
                            {(field.hasComment && isTicked) &&
                                <TextField
                                    sx={{mt: 1, ml: 4.5, width: 'calc(100% - 36px)', backgroundColor: '#fff'}}
                                    label={'Comment'}
                                    multiline
                                    value={selectedOptions.find(option => option.id === field.id)?.comment}
                                    autoComplete="off"
                                    disabled={readonly}
                                    onChange={event => {
                                        handleAddComment(event.target.value, field.id);
                                    }}
                                />
                            }
                        </Box>
                    );
                })}
                </Card>
            )}
        </ConfirmationWrapper>
    );
};

WelfareOptions.propTypes = {
    readonly: PropTypes.bool,
    onConfirmSuccess: PropTypes.func,
    onDataLoading: PropTypes.func,
};
export default WelfareOptions;